import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Main from './session/main';
import { MainSync, MultiExperimentalSync } from './session/main-sync';
import Versus from './session/versus';
import { sessionActions } from '../redux/_actions';
import {
  sessionLoadingState,
  sessionViewType,
  uiState,
} from '../redux/_constants';
import { SessionType } from '../models';
import { useHistory, useLocation } from 'react-router-dom';
import FullscreenLoading from './helpers/fullscreenloading';
import ProducerLoading from './helpers/producerloading';
import { useQueryParams } from '../utilities/query';
import ScoresJSON from './session/scoresJSON';
import ScoreSlips from './session/scoreslips';
import LiveClipsJSON from './session/liveclipsJSON';
import ScoreboardDisplay from './session/scoreboarddisplay';
import Producer from './session/producer';
import SessionNotFoundPage from './session/not-found-page';
import UnauthorizedPage from './session/unauthorized-page';
import ScoreConverter from './session/scoreconverter';
import ScoreSheet from './session/scoresheet';
import OfficialScoreSheet from './session/officialscoresheet';
import ClipConverter from './session/clipconverter';
import { useApolloClient } from '@apollo/client';
import GetRoutines from '../apollo/queries/GetRoutines.graphql';
import {
  useRoleBasedSessionQuery,
  useHasSensitiveAccess,
} from './session/useRoleBasedSessionQuery';
import { useDocumentTitle } from '../utilities/hooks';
import { useNetwork } from '../utilities/network';
import { alertActions } from '../redux/_actions';
import { useErrorHandler } from '../apollo/helpers';
import MultiExperimental from './session/multiexperimental';
import {
  sessionDebugger,
  debugStep,
  SESSION_LOAD_STEPS,
} from '../utilities/session-debug';
import { wrapApolloClient } from '../utilities/apollo-debug-wrapper';

function Session() {
  const setTitle = useDocumentTitle();
  const sessionKey = useSelector((state) => state.session.sessionKey);
  const sessionId = useSelector((state) => state.session.id);
  const sessionName = useSelector((state) => state.session.name);
  const sessionType = useSelector((state) => state.session.type);
  const featureFlags = useSelector((state) => state.session.featureFlags || {});
  const loadingState = useSelector((state) => state.session.loadingState);
  const networkState = useNetwork({ polling: false });

  const producerLineupAId = useSelector(
    (state) => state.producer?.teamA?.lineupId
  );
  const producerLineupBId = useSelector(
    (state) => state.producer?.teamB?.lineupId
  );
  const sessionLineupAId = useSelector(
    (state) =>
      state.session.lineups?.items.find((l) => l.id === producerLineupAId)?.id
  );
  const sessionLineupBId = useSelector(
    (state) =>
      state.session.lineups?.items.find((l) => l.id === producerLineupBId)?.id
  );

  const handlePrefetchSessionError = useErrorHandler(
    'There was a problem prefetching the session'
  );

  const history = useHistory();
  const queryParams = useQueryParams();
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const location = useLocation();

  // Check if user has access to sensitive data (ADMIN/JUDGE only)
  const hasSensitiveAccess = useHasSensitiveAccess();

  const [prefetch, setPrefetch] = useState();
  const [routines, setRoutines] = useState();

  const queryMirror = queryParams.has('mirror');
  const queryMirrorSide = queryParams.get('mirror');
  const queryConvertLineupDataScores = queryParams.has(
    'convertlineupdatascores'
  );
  const queryProducer = queryParams.has('producer');
  const queryTheme = queryParams.has('theme');
  const isDarkMode =
    queryTheme &&
    ['1', 'dark'].includes(String(queryParams.get('theme')).toLowerCase());
  const querySizeType = queryParams.get('size');
  const queryProducerType = queryParams.get('producer');
  const queryStage = queryParams.get('stage');
  const querySquad = queryParams.get('squad');
  const queryConvertClips = queryParams.has('convertclips');
  const queryLiveClips = queryParams.has('liveclips');
  const queryScoreSheet = queryParams.has('scoresheet');
  const queryVersus = queryParams.has('versus');
  const queryScoreSlips = queryParams.has('scoreslips');
  const queryOfficialScoreSheet = queryParams.has('officialscoresheet');
  const queryLeaderBoard = queryParams.has('leaderboard');
  const queryLeaderBoardType = queryParams.get('leaderboard');
  const queryScoreBoard = queryParams.has('scoreboard');
  const queryScoreBoardType = queryParams.get('scoreboard');
  const queryScoresData = queryParams.has('scores');
  const queryFullScreen = queryParams.has('fullscreen');
  const queryFullScreenSide = queryParams.get('fullscreen');
  const querySoloPlayer = queryParams.has('soloplayer');
  const queryExperimental =
    queryParams.has('beta') || queryParams.has('evalslab');
  const queryMobile = queryParams.has('mobile');
  const queryStreamType = queryParams.get('stream');
  const querySessionKey = queryParams.get('s');

  // Check for Lambda-injected session data (for PDF generation)
  const hasInjectedData =
    typeof window !== 'undefined' && !!window.__INJECTED_SESSION_DATA__;

  // Use role-based session query that automatically selects appropriate query
  const roleBasedSessionQuery = useRoleBasedSessionQuery({
    sessionId: null, // We don't have sessionId yet, use sessionKey
    sessionKey: querySessionKey,
    queryOptions: {
      skip: !querySessionKey || hasInjectedData, // Skip if no key OR if Lambda injected data
      errorPolicy: 'all',
    },
  });
  const isLoaded = querySessionKey === sessionKey;
  // The producer state is not currently cleared when we navigate away from a session
  // so the lineup IDs it has may not be the ones from the session we want to load for
  // the current page. We check just one of the producer lineup ids here since they're
  // set at the same time.
  const isProducerInitialized =
    isLoaded &&
    !!producerLineupAId &&
    (producerLineupAId === sessionLineupAId ||
      producerLineupAId === sessionLineupBId);
  const isScoreboardDataInitialized = queryScoreBoardType && routines;
  const isMulti = sessionType === SessionType.MULTI;
  const shouldSync = queryParams.get('sync') === '1';
  // Check if this is PDF mode (has pdfToken)
  const isPdfMode = queryParams.get('pdfToken');

  // Debug: Log PDF token detection
  if (hasInjectedData) {
    console.log(
      '[PDF Session] pdfToken value from queryParams:',
      queryParams.get('pdfToken')
    );
    console.log(
      '[PDF Session] current URL:',
      typeof window !== 'undefined' ? window.location.href : 'N/A'
    );
  }

  const readyToRender =
    // For PDF mode, we're ready as soon as we have prefetch data (bypass all loading checks)
    (isPdfMode && prefetch) ||
    // Otherwise use normal loading checks
    (isLoaded &&
      isProducerInitialized &&
      loadingState === sessionLoadingState.LOADED &&
      prefetch &&
      (!queryScoreBoardType || isScoreboardDataInitialized));

  // Debug logging for PDF mode
  if (hasInjectedData) {
    console.log('[PDF Session] hasInjectedData:', hasInjectedData);
    console.log(
      '[PDF Session] prefetch:',
      prefetch ? `yes (${prefetch.name})` : 'NO'
    );
    console.log('[PDF Session] isPdfMode:', isPdfMode ? 'YES' : 'NO');
    console.log('[PDF Session] isLoaded:', isLoaded);
    console.log('[PDF Session] isProducerInitialized:', isProducerInitialized);
    console.log('[PDF Session] loadingState:', loadingState);
    console.log('[PDF Session] readyToRender:', readyToRender);
    console.log(
      '[PDF Session] queryOfficialScoreSheet:',
      queryOfficialScoreSheet
    );
    console.log('[PDF Session] hasSensitiveAccess:', hasSensitiveAccess);
  }

  // Initialize session debugging when session key changes
  useEffect(() => {
    if (querySessionKey) {
      sessionDebugger.startSession(querySessionKey);
      // Wrap Apollo client for debugging
      wrapApolloClient(apolloClient);
    }
  }, [querySessionKey, apolloClient]);

  // Track readyToRender state changes
  useEffect(() => {
    if (readyToRender) {
      debugStep(SESSION_LOAD_STEPS.READY_TO_RENDER, {
        sessionKey: querySessionKey,
        sessionId,
        sessionName,
        isLoaded,
        isProducerInitialized,
        loadingState,
        hasPrefetch: !!prefetch,
        isScoreboardDataInitialized,
      });
    }
  }, [
    readyToRender,
    querySessionKey,
    sessionId,
    sessionName,
    isLoaded,
    isProducerInitialized,
    loadingState,
    prefetch,
    isScoreboardDataInitialized,
  ]);

  // Probe for blockers if we remain not ready after prefetch
  useEffect(() => {
    const needsRoutines = !!queryScoreBoardType || !!queryLiveClips;
    if (prefetch && !readyToRender) {
      const t = setTimeout(() => {
        if (!readyToRender) {
          debugStep('READY_BLOCKED', {
            querySessionKey,
            sessionKey,
            sessionId,
            loadingState,
            isLoaded,
            hasPrefetch: !!prefetch,
            producerLineupAId,
            sessionLineupAId,
            producerLineupBId,
            sessionLineupBId,
            isProducerInitialized,
            needsRoutines,
            hasRoutines: !!routines,
          });
        }
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [
    prefetch,
    readyToRender,
    querySessionKey,
    sessionKey,
    sessionId,
    loadingState,
    isLoaded,
    producerLineupAId,
    sessionLineupAId,
    producerLineupBId,
    sessionLineupBId,
    isProducerInitialized,
    queryScoreBoardType,
    queryLiveClips,
    routines,
  ]);

  // Track component mounting
  useEffect(() => {
    if (querySessionKey) {
      debugStep(SESSION_LOAD_STEPS.COMPONENT_MOUNT, {
        sessionKey: querySessionKey,
        url: window.location.href,
        queryParams: Array.from(queryParams.entries()).reduce(
          (acc, [k, v]) => ({ ...acc, [k]: v }),
          {}
        ),
      });
    }
  }, []); // Only run once on mount

  useEffect(() => {
    if (sessionId) {
      dispatch(sessionActions.subscribe(sessionId));
      dispatch(sessionActions.subscribeNewStream(sessionId));
      dispatch(sessionActions.subscribeAllStreams());
      dispatch(sessionActions.subscribeAllLineups());
      return () => {
        dispatch(sessionActions.unsubscribe(sessionId));
        dispatch(sessionActions.unsubscribeNewStream(sessionId));
        dispatch(sessionActions.unsubscribeAllStreams());
        dispatch(sessionActions.unsubscribeAllLineups());
      };
    }
  }, [dispatch, sessionId]);

  useEffect(() => {
    if (!networkState.online) {
      console.log('Session is currently offline');
      dispatch(alertActions.offline('Check connection and refresh.'));
    }

    if (networkState.online) {
      console.log('Session is currently online');
      dispatch(alertActions.clear());
    }
  }, [dispatch, networkState.online]);

  // Handle Lambda-injected session data OR role-based session query results
  useEffect(() => {
    // If Lambda injected data (for PDF), use it directly
    if (hasInjectedData && window.__INJECTED_SESSION_DATA__) {
      console.log('[PDF] Using injected session data in Session component');
      setPrefetch(window.__INJECTED_SESSION_DATA__);
      return;
    }

    // Otherwise handle GraphQL query results
    if (roleBasedSessionQuery.data?.SessionByKey?.items) {
      const items = roleBasedSessionQuery.data.SessionByKey.items;
      if (items.length === 0) {
        // more ideally, we turn this page into a 404
        history.push(uiState.START);
        return;
      }
      setPrefetch(items[0]);
    }

    if (roleBasedSessionQuery.error && !roleBasedSessionQuery.loading) {
      handlePrefetchSessionError(roleBasedSessionQuery.error, true);
    }
  }, [
    hasInjectedData,
    roleBasedSessionQuery.data,
    roleBasedSessionQuery.error,
    roleBasedSessionQuery.loading,
    history,
    setPrefetch,
    handlePrefetchSessionError,
  ]);

  useEffect(() => {
    let isCancelled = false;

    if (sessionId && (queryScoreBoardType || queryLiveClips)) {
      apolloClient
        .query({
          query: GetRoutines,
          variables: { sessionId },
        })
        .then((result) => {
          if (!isCancelled && result?.data?.SessionRoutines?.items) {
            setRoutines(result.data.SessionRoutines.items);
            /*setRoutines(
              result.data.SessionRoutines.items
                .filter(
                  (routine) =>
                    routine.apparatus === queryScoreBoardType.toUpperCase() &&
                    routine.status !== RoutineStatus.DELETED
                )
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            );*/
          }
        })
        .catch((error) => {
          if (!isCancelled) {
            console.error('Error fetching routines:', error);
            // Set empty array as fallback to prevent undefined access
            setRoutines([]);
          }
        });
      return () => {
        isCancelled = true;
      };
    }
  }, [
    apolloClient,
    sessionId,
    queryScoreBoardType,
    queryLiveClips,
    setRoutines,
  ]);

  useEffect(() => {
    // For PDF mode with injected data, load directly from the injected data
    if (hasInjectedData && window.__INJECTED_SESSION_DATA__) {
      console.log('[PDF] Loading session from injected data into Redux');
      dispatch(
        sessionActions.loadFromInjectedData(window.__INJECTED_SESSION_DATA__)
      );

      // Also write to Apollo cache
      console.log('[PDF] Writing injected data to Apollo cache');
      apolloClient.writeQuery({
        query: require('../apollo/queries/GetFullSession.graphql'),
        variables: { id: window.__INJECTED_SESSION_DATA__.id },
        data: { getSession: window.__INJECTED_SESSION_DATA__ },
      });
    } else {
      // Normal flow: dispatch load action which fetches from GraphQL
      dispatch(sessionActions.load(querySessionKey));
    }

    return () => {
      dispatch(sessionActions.unload());
    };
  }, [dispatch, querySessionKey, hasInjectedData, apolloClient]);

  useEffect(() => {
    if (!isLoaded || queryOfficialScoreSheet) {
      return undefined;
    }
    setTitle(sessionName);
    return undefined;
  }, [isLoaded, sessionName, setTitle, queryOfficialScoreSheet]);

  // useEffect(() => {
  //   if (isLoaded) {
  //     dispatch(sessionActions.subscribeNewStream(sessionId));
  //     return () => {
  //       dispatch(sessionActions.unsubscribeNewStream(sessionId));
  //     };
  //   }
  // }, [dispatch, isLoaded, sessionId]);

  // useEffect(() => {
  //   if (isProducerInitialized) {
  //     dispatch(sessionActions.subscribeAllStreams());
  //     return () => {
  //       dispatch(sessionActions.unsubscribeAllStreams());
  //     };
  //   }
  // }, [dispatch, isProducerInitialized]);

  // useEffect(() => {
  //   if (isProducerInitialized) {
  //     dispatch(sessionActions.subscribeAllLineups());
  //     return () => {
  //       dispatch(sessionActions.unsubscribeAllLineups());
  //     };
  //   }
  // }, [dispatch, isProducerInitialized]);

  useEffect(() => {
    if (readyToRender && queryScoreBoard) {
      dispatch(
        sessionActions.changeView(
          sessionViewType.SCOREBOARD,
          queryScoreBoardType
        )
      );
    }
  }, [dispatch, readyToRender, queryScoreBoard, queryScoreBoardType]);

  useEffect(() => {
    if (readyToRender && querySoloPlayer) {
      dispatch(sessionActions.changeView(sessionViewType.SOLOPLAYER));
    }
  }, [dispatch, readyToRender, querySoloPlayer]);

  // Handle Full Screen
  useEffect(() => {
    if (readyToRender && queryFullScreen) {
      dispatch(
        sessionActions.changeView(
          sessionViewType.FULLSCREEN,
          queryFullScreenSide
        )
      );
    }
  }, [dispatch, readyToRender, queryFullScreen, queryFullScreenSide]);

  // Handle mobile view
  useEffect(() => {
    if (readyToRender && queryMobile) {
      dispatch(sessionActions.changeView(sessionViewType.MOBILE));
    }
  }, [dispatch, readyToRender, queryMobile]);

  // Handle mirror view
  useEffect(() => {
    if (queryMirror) {
      dispatch(
        sessionActions.changeView(sessionViewType.MIRROR, queryMirrorSide)
      );
    }
  }, [dispatch, queryMirror, queryMirrorSide]);

  useEffect(() => {
    if (!querySessionKey) {
      history.push(uiState.START);
    }
  }, [history, querySessionKey]);

  if (readyToRender) {
    if (queryScoresData) {
      return <ScoresJSON sessionId={prefetch.id} />;
    } else if (queryLiveClips) {
      return <LiveClipsJSON sessionId={prefetch.id} />;
    } else if (queryConvertLineupDataScores) {
      return <ScoreConverter sessionId={prefetch.id} />;
    } else if (queryConvertClips) {
      return <ClipConverter sessionId={prefetch.id} />;
    } else if (queryScoreSheet) {
      return <ScoreSheet sessionId={prefetch.id} />;
    } else if (queryScoreSlips) {
      return <ScoreSlips sessionId={prefetch.id} />;
    } else if (
      queryOfficialScoreSheet ||
      uiState.SCORESHEET.includes(location.pathname)
    ) {
      if (hasSensitiveAccess) {
        return <OfficialScoreSheet sessionId={prefetch.id} />;
      } else {
        console.warn(
          '[Session Router] OfficialScoreSheet access denied - rendering Main instead'
        );
        return (
          <Main
            sessionId={prefetch.id}
            session={prefetch}
            isDarkMode={isDarkMode}
            isLeaderBoard={queryLeaderBoard}
            streamType={queryStreamType}
          />
        );
      }
    } else if (queryScoreBoardType) {
      if (queryScoreBoardType.toUpperCase() === 'HEAD2HEAD') {
        if (shouldSync) {
          return (
            <MainSync
              sessionId={prefetch.id}
              isDarkMode={isDarkMode}
              isLeaderBoard={queryLeaderBoard}
              streamType={queryStreamType}
            />
          );
        }
        return (
          <Main
            sessionId={prefetch.id}
            session={prefetch}
            isDarkMode={isDarkMode}
            isLeaderBoard={queryLeaderBoard}
            streamType={queryStreamType}
          />
        );
      }
      return (
        <ScoreboardDisplay
          sessionId={prefetch.id}
          type={queryScoreBoardType}
          isDarkMode={isDarkMode}
          size={querySizeType}
        />
      );
    } else if (queryProducer) {
      return (
        <Producer
          sessionId={prefetch.id}
          loadType={queryProducerType}
          initialStage={queryStage}
          initialSquad={querySquad}
          isDarkMode={isDarkMode}
        />
      );
    } else if (queryLeaderBoard) {
      return (
        <Main
          sessionId={prefetch.id}
          session={prefetch}
          isDarkMode={isDarkMode}
          isLeaderBoard={queryLeaderBoard}
          size={querySizeType}
          leaderboardType={queryLeaderBoardType}
        />
      );
    } else if (
      featureFlags.multiExperimental ||
      queryExperimental ||
      isMulti ||
      [
        'gHkk63qktV', // WCU Purple & Gold '25
        'v4CTXi_-05', // W&M Intrasquad '25 (M)
        'ZpgxsJ_rVS', // W&M Intrasquad '25 (W) (Copy)
        'kahw-E7gAc', // W&M Intrasquad '25 (W)
        'iJ_2tgT5pk', // Battle of the Bay (Session 1)
        'DPhL8w-Yyk', // Battle of the Bay (Session 2)
        'hPCo7KChNE', // Southern Utah Tri '24
        '1lTtBbL1G3', // Denver/OU/UCLA/Stanford '24
        'KTjsvZNTHL', // W&M Intrasquad '24 (W)
        'fmm1tw3VTL', // H2H Testintrasquad
        'F5JFJxrqG-', // Penn intrasquad
        'KOOCeNZ_4X', // Red rocks preview Utah
        'gVK4F67dPg', // Arizona Red & Blue
        '6GcfgdsBYV', // BGSU intrasquad
        'lcNDT5Ozmj', // Arkansas Intrasquad
        '3BGoE0eNKL', // Minnesota Intrasquad
        'oo8GWgi1-G', // Pitt Intrasquad
        'I3l1eOEA7h',
      ].includes(sessionKey) // from Battle of the Bay x 4 and W&M Women's Hybrid h2h+alternating intrasquad, Penn Intrasquad
    ) {
      if (shouldSync) {
        return <MultiExperimentalSync sessionId={prefetch.id} />;
      }

      return <MultiExperimental sessionId={prefetch.id} session={prefetch} />;
    } else if (queryVersus) {
      return (
        <Versus
          sessionId={prefetch.id}
          session={prefetch}
          isDarkMode={isDarkMode}
          isLeaderBoard={queryLeaderBoard}
          streamType={queryStreamType}
        />
      );
    } else if (shouldSync) {
      return (
        <MainSync
          sessionId={prefetch.id}
          isDarkMode={isDarkMode}
          isLeaderBoard={queryLeaderBoard}
          streamType={queryStreamType}
        />
      );
    } else {
      return (
        <Main
          sessionId={prefetch.id}
          session={prefetch}
          isDarkMode={isDarkMode}
          isLeaderBoard={queryLeaderBoard}
          streamType={queryStreamType}
        />
      );
    }
  } else if (loadingState === sessionLoadingState.UNAUTHORIZED) {
    return <UnauthorizedPage />;
  } else if (loadingState === sessionLoadingState.NOT_FOUND) {
    return <SessionNotFoundPage />;
  } else {
    if (queryProducer) {
      return <ProducerLoading />;
    } else {
      return (
        <FullscreenLoading
          message="Loading session..."
          isDarkMode={isDarkMode}
        />
      );
    }
  }
}

export default Session;
